import clsx from 'clsx';
import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@bymason-private/auth-provider';
import {
  CreateGroupModal,
  DEFAULT_BOTTOM_SCROLL_THRESHOLD,
  DEFAULT_PAGE_SIZE,
  useToast,
} from '@bymason-private/controller-shared-widgets';
import { makeStyles } from '@bymason-private/controller-v1-theme-provider';
import { Button } from '@bymason-private/legacy-pattern-components';
import { useRouter } from '@bymason-private/route-provider';
import { GroupModel, useLoadInfiniteGroups } from '@bymason/controller-api-hooks';
import { useIsScrolledToBottom } from '@bymason/util-hooks';
import { formatLongDateTimeString, removeQueryParamFromCurrentUrl } from '@bymason/utils';
import InfoCard, { DetailItem } from '../../common/info-card';
import Layout from '../../common/layout';
import { routes } from '../../router';
import NoGroups from './no-groups';

const useStyles = makeStyles(() => ({
  groupsSection: {
    width: '100%',
    padding: `8px 0 16px 0`,
  },
  groupsSectionHasData: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    columnGap: 20,
    rowGap: 20,
  },

  '@media (max-width: 1170px)': {
    groupsSectionHasData: {
      gridTemplateColumns: 'repeat(1, 1fr)',
    },
  },
}));

export default function GroupsView() {
  const { authToken = '' } = useAuth();
  const [setToastMessage, setToastSeverity] = useToast();
  const { navigate } = useRouter();
  const classes = useStyles();
  const [isScrolledToBottom] = useIsScrolledToBottom(DEFAULT_BOTTOM_SCROLL_THRESHOLD);
  const [groups, groupsError, groupsBusy] = useLoadInfiniteGroups(
    authToken,
    DEFAULT_PAGE_SIZE,
    isScrolledToBottom
  );

  const [showCreateGroupModal, setShowCreateGroupModal] = useState(
    new URLSearchParams(window?.location.search).has('create')
  );

  const handleCreateGroupModalOpen = () => {
    setShowCreateGroupModal(true);
  };

  const handleCreateGroupModalClose = () => {
    setShowCreateGroupModal(false);
  };

  const handleCardClick = useCallback(
    (name: string) => (evt: React.MouseEvent<HTMLAnchorElement>) => {
      evt.preventDefault();
      navigate(routes.Group, { name });
    },
    [navigate]
  );

  const handleCreateGroupSuccess = useCallback(
    (newGroup: GroupModel) => {
      if (newGroup) {
        setShowCreateGroupModal(false);
        setToastSeverity('success');
        setToastMessage(`Successfully created group "${newGroup.name}"`);
        navigate(routes.Group, { name: newGroup.name });
      }
    },
    [navigate, setToastSeverity, setToastMessage]
  );

  const handleCreateGroupError = useCallback(
    (errorMessage: string) => {
      if (errorMessage) {
        setToastSeverity('error');
        setToastMessage(errorMessage);
      }
    },
    [setToastSeverity, setToastMessage]
  );

  useEffect(() => {
    removeQueryParamFromCurrentUrl('create');
  }, []);

  useEffect(() => {
    const errorMessage = groupsError;

    if (errorMessage) {
      setToastSeverity('error');
      setToastMessage(errorMessage);
    }
  }, [groupsError, setToastMessage, setToastSeverity]);

  return (
    <Layout
      busy={groupsBusy}
      headerText='Groups'
      headerRightAdornment={
        <Button variant='outlined' onClick={handleCreateGroupModalOpen}>
          Create group
        </Button>
      }
      stickyHeader
    >
      <section
        className={clsx({
          [classes.groupsSection]: true,
          [classes.groupsSectionHasData]: groups && groups.length > 0 ? true : false,
        })}
      >
        {groups && groups.length > 0
          ? groups.map((info) => {
              const detailInfo: DetailItem[] = info.apiLevel
                ? [
                    {
                      type: 'devices',
                      value: info.deviceFamily || 'No devices',
                    },
                    {
                      type: 'api',
                      value: info.apiLevel.toString() || '-',
                    },
                    {
                      type: 'createdAt',
                      value: formatLongDateTimeString(info.createdAt),
                    },
                  ]
                : [
                    {
                      type: 'devices',
                      value: info.deviceFamily || 'No devices',
                    },
                    {
                      type: 'createdAt',
                      value: formatLongDateTimeString(info.createdAt),
                    },
                  ];
              return (
                <InfoCard
                  key={info.id}
                  title={info.name}
                  description={info.description}
                  detailItems={detailInfo}
                  href={`${window.location.pathname}/${info.name}`}
                  onClick={handleCardClick(info.name)}
                />
              );
            })
          : !groupsBusy && !groupsError && <NoGroups onCreateGroupClick={handleCreateGroupModalOpen} />}
      </section>
      <CreateGroupModal
        open={showCreateGroupModal}
        onClose={handleCreateGroupModalClose}
        onSuccess={handleCreateGroupSuccess}
        onError={handleCreateGroupError}
      />
    </Layout>
  );
}
